import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { pulisci } from '../../lib/richtext';

/**
 * Campo di testo formattato.
 *
 * Serve a un documento che si legge, non a una pagina che si impagina: si puo'
 * fare un titolo, un elenco, mettere in evidenza una parola e poco altro. La
 * barra propone solo quello, cosi' due regolamenti scritti da due
 * amministratori diversi si assomigliano ancora.
 *
 * Dentro e' un `contentEditable`, e i comandi passano da `document.execCommand`:
 * e' deprecato da anni ma non e' mai stato sostituito, e resta l'unico modo per
 * dire al browser «metti in grassetto quello che l'utente ha selezionato»
 * lasciando a lui la gestione del cursore, che e' la parte difficile.
 */

/** Cio' che si puo' fare, nell'ordine in cui compare in barra. */
const COMANDI = [
    { nome: 'bold', icona: 'bold', titolo: 'Grassetto' },
    { nome: 'italic', icona: 'italic', titolo: 'Corsivo' },
    { nome: 'underline', icona: 'underline', titolo: 'Sottolineato' },
    { blocco: 'h2', icona: 'heading', titolo: 'Titolo' },
    { blocco: 'h3', icona: 'heading', titolo: 'Sottotitolo', piccolo: true },
    { nome: 'insertUnorderedList', icona: 'list-ul', titolo: 'Elenco puntato' },
    { nome: 'insertOrderedList', icona: 'list-ol', titolo: 'Elenco numerato' }
];

export default function RichText({ value, onChange, ariaLabel, disabled, placeholder }) {
    const areaRef = useRef(null);
    const [attivi, setAttivi] = useState({});

    /*
     * L'HTML si riscrive solo quando arriva da fuori (il caricamento del
     * record, o un annulla). Riscriverlo a ogni battuta rifarebbe il nodo da
     * capo sotto le dita di chi scrive, e il cursore tornerebbe in cima.
     */
    useEffect(() => {
        const area = areaRef.current;
        if (!area) return;
        const dentro = value == null ? '' : value;
        if (area.innerHTML !== dentro) area.innerHTML = dentro;
    }, [value]);

    /* Invio crea un paragrafo, non un `div`: e' l'unico blocco che sappiamo
       ripulire, impaginare e riaprire senza sorprese. */
    useEffect(() => {
        try {
            document.execCommand('defaultParagraphSeparator', false, 'p');
        }
        catch (err) {
            // browser che non lo conosce: si tiene il suo separatore
        }
    }, []);

    /** Quali comandi sono accesi dove sta il cursore. */
    const aggiornaStato = useCallback(() => {
        const area = areaRef.current;
        if (!area || document.activeElement !== area) return;
        const stato = {};
        COMANDI.forEach(c => {
            if (c.nome) {
                try { stato[c.nome] = document.queryCommandState(c.nome); }
                catch (err) { stato[c.nome] = false; }
            }
        });
        const blocco = bloccoCorrente(area);
        COMANDI.forEach(c => { if (c.blocco) stato[c.blocco] = blocco === c.blocco; });
        setAttivi(stato);
    }, []);

    useEffect(() => {
        document.addEventListener('selectionchange', aggiornaStato);
        return () => document.removeEventListener('selectionchange', aggiornaStato);
    }, [aggiornaStato]);

    function notifica() {
        if (areaRef.current) onChange(areaRef.current.innerHTML);
    }

    function esegui(comando, valore) {
        const area = areaRef.current;
        if (!area || disabled) return;
        area.focus();
        try { document.execCommand(comando, false, valore); }
        catch (err) { return; }
        aggiornaStato();
        notifica();
    }

    /** Il titolo si toglie ridiventando paragrafo, come ovunque. */
    function alterna(c) {
        if (c.nome) return esegui(c.nome);
        const gia = bloccoCorrente(areaRef.current) === c.blocco;
        return esegui('formatBlock', gia ? '<p>' : `<${c.blocco}>`);
    }

    function collega() {
        const area = areaRef.current;
        if (!area || disabled) return;
        const selezione = window.getSelection();
        if (!selezione || selezione.isCollapsed) {
            window.alert('Seleziona prima il testo da collegare.');
            return;
        }
        const url = window.prompt('Indirizzo del collegamento:', 'https://');
        if (!url) return;
        // la ripulitura scarta gli href che non le piacciono: meglio dirlo subito
        if (!/^(https?:|mailto:|tel:|#|\/)/i.test(url)) {
            window.alert('Indirizzo non valido: serve un http://, https://, mailto: o tel:.');
            return;
        }
        esegui('createLink', url);
    }

    /*
     * Incollare porta dentro l'HTML di dove si copiava — fogli di stile di
     * Word, span colorati, caratteri di un altro documento. Entra il solo
     * testo: la formattazione la si rifa' con la barra, ed e' un attimo.
     */
    function incolla(e) {
        e.preventDefault();
        const testo = (e.clipboardData || window.clipboardData).getData('text/plain');
        esegui('insertText', testo);
    }

    const vuoto = !value || value === '<p></p>' || value === '<br>';

    return (
        <div className={`lv-rt${disabled ? ' lv-rt-disabled' : ''}`}>
            <div className="lv-rt-bar" role="toolbar" aria-label="Formattazione">
                {COMANDI.map(c => (
                    <button
                        key={c.nome || c.blocco}
                        type="button"
                        className={`lv-rt-btn${attivi[c.nome || c.blocco] ? ' is-attivo' : ''}${c.piccolo ? ' lv-rt-btn-sm' : ''}`}
                        title={c.titolo}
                        aria-label={c.titolo}
                        aria-pressed={Boolean(attivi[c.nome || c.blocco])}
                        disabled={disabled}
                        // premere un tasto della barra non deve togliere la
                        // selezione: e' proprio quella che si sta formattando
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => alterna(c)}
                    >
                        <FontAwesomeIcon icon={['fas', c.icona]} />
                    </button>
                ))}
                <span className="lv-rt-sep" />
                <button
                    type="button"
                    className="lv-rt-btn"
                    title="Collegamento"
                    aria-label="Collegamento"
                    disabled={disabled}
                    onMouseDown={e => e.preventDefault()}
                    onClick={collega}
                >
                    <FontAwesomeIcon icon={['fas', 'link']} />
                </button>
                <button
                    type="button"
                    className="lv-rt-btn"
                    title="Togli la formattazione"
                    aria-label="Togli la formattazione"
                    disabled={disabled}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => { esegui('removeFormat'); esegui('unlink'); }}
                >
                    <FontAwesomeIcon icon={['fas', 'eraser']} />
                </button>
            </div>

            <div
                ref={areaRef}
                className="lv-rt-area"
                role="textbox"
                aria-multiline="true"
                aria-label={ariaLabel}
                contentEditable={!disabled}
                suppressContentEditableWarning
                data-placeholder={placeholder || 'Scrivi qui il testo…'}
                data-vuoto={vuoto ? '1' : '0'}
                onInput={notifica}
                onBlur={() => { if (areaRef.current) onChange(pulisci(areaRef.current.innerHTML)); }}
                onKeyUp={aggiornaStato}
                onMouseUp={aggiornaStato}
                onPaste={incolla}
            />
        </div>
    );
}

/** Tag del blocco in cui sta il cursore, minuscolo. */
function bloccoCorrente(area) {
    const selezione = window.getSelection();
    if (!area || !selezione || selezione.rangeCount === 0) return '';
    let nodo = selezione.getRangeAt(0).startContainer;
    while (nodo && nodo !== area) {
        if (nodo.nodeType === 1 && /^(P|H2|H3|LI|BLOCKQUOTE)$/.test(nodo.tagName)) {
            return nodo.tagName.toLowerCase();
        }
        nodo = nodo.parentNode;
    }
    return '';
}
